import type { JSX } from 'react'
import type { ShowWithPrice, VodWithPrice } from '~/shared/interfaces'
import type { BannerBundle } from './index.h'

import { BannerEmptyLarge } from "/app/libraries/arcana/src/blocks/Banner/Templates/BannerEmptyType/index.ts"
import { CategoryBannerCardTemplate } from "/app/libraries/arcana/src/containers/RowContainer/Templates/index.ts"

import { ResourceTypes } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { bannerSlide, model as routerModel } from '~/core/router'
import {
  getBannerAgeRatingLabel,
  getBannerContentLabels,
  getBannerTopContentLabels,
} from '~/entities/banners/lib'
import { TrailerContentType } from '~/entities/player/trailer/index.h'
import * as entitiesPlayerTrailerModel from '~/entities/player/trailer/model'
import {
  model as favoritesFeatureModel,
  model as favoritesModel,
} from '~/features/favorites'
import {
  isFree as getIsFree,
  isPaid,
  model as paymentModel,
} from '~/features/payment'
import * as pinLockFeature from '~/features/pin-lock'
import { $signInToWatchTranslations } from '~/features/signin-to-watch'
import { model as guest } from '~/features/subscriber/guest'
import {
  $vodCardHorizontalCovers,
  $vodCardVerticalCovers,
} from '~/shared/ui/components/cardCovers/vodCardCovers'
import {
  $detailsTitle,
  $playTitle,
  $trailerTitle,
} from '~/shared/ui/translations'
import { model as externalPaymentModel } from '~/widgets/Payment/ExternalPaymentOpen'
import * as paymentWidgetModel from '~/widgets/Payment/model'
import { model as signInToWatchPopUpModel } from '~/widgets/Popups/SignInToWatchPopup'
import { buildBannerVisibleParams } from './buildBannerVisibleParams'
import { ChannelBannerCard } from './ChannelBannerCard'
import { ShowBannerCard } from './ShowBannerCard'
import { VodBannerCard } from './VodBannerCard'

type SubCategory =
  | (VodWithPrice & { parentCategoryId: string })
  | (ShowWithPrice & { parentCategoryId: string })

export type BannerCardPlacement = {
  categoryId: number
  categoryName: string
  collectionId?: string
  onlyFavorites?: boolean
  detailsTitle?: string
}

export type BannerCarouselData = {
  content: BannerBundle[] | undefined
  bannerId: number | string | undefined
  autoScrollIn?: number
  autoScrollEnabled?: boolean
}

export function useBannerCarouselCards(
  placement: BannerCardPlacement,
  carouselData: BannerCarouselData
) {
  const { content, bannerId, autoScrollIn, autoScrollEnabled } = carouselData
  const locationKey = useUnit(routerModel.$key)

  const { t } = useTranslation()
  const lockedTitle = t('button_unlock')
  const playTitle = useUnit($playTitle)
  const detailsTitleFromStore = useUnit($detailsTitle)
  const trailerTitle = useUnit($trailerTitle)
  const detailsTitle = placement.detailsTitle ?? detailsTitleFromStore

  const getIsBannerAutoScrollEnabled = useUnit(
    pinLockFeature.$isBannerAutoScrollEnabled
  )
  const getIsLockedFn = useUnit(pinLockFeature.$isLockedFn)
  const getLockedBannerSubtitle = useUnit(pinLockFeature.$lockedBannerSubtitle)
  const getLockedBannerHint = useUnit(pinLockFeature.$lockedBannerHint)
  const getIsShowHowToWatchButton = useUnit(
    paymentWidgetModel.$showHowToWatchButton
  )
  const howToWatchTitle = useUnit(
    externalPaymentModel.$externalPayment
  )?.howToWatch
  const isAnyPaymentPopupOpened = useUnit(paymentModel.$isAnyPaymentPopupOpened)
  const horizontalCovers = useUnit($vodCardHorizontalCovers)
  const verticalCovers = useUnit($vodCardVerticalCovers)
  const getShowSignInToWatchButton = useUnit(
    paymentWidgetModel.$showSignInToWatchButton
  )
  const signInToWatchTitle = useUnit($signInToWatchTranslations)?.signInToWatch
  const isGuest = useUnit(guest.$guest)
  const favorites = useUnit(favoritesFeatureModel.$favorites)
  const onFavoriteClick = useUnit(favoritesModel.favorite)
  const onSignInClick = useUnit(signInToWatchPopUpModel.openSignInToWatchPopup)
  const onLockedClick = useUnit(pinLockFeature.unlockContent)

  const [
    autoScrollEnabledForTrailer,
    goToNextSlideTrigger,
    isTrailerPlayerVisible,
    onBannerSlideChanged,
    onBannerBecameVisible,
    shouldRenderTrailerPlayer,
    getSlotRefCallback,
    trailerContentId,
  ] = useUnit([
    entitiesPlayerTrailerModel.$autoScrollEnabledForTrailer,
    entitiesPlayerTrailerModel.$goToNextSlideTrigger,
    entitiesPlayerTrailerModel.$isTrailerPlayerVisible,
    entitiesPlayerTrailerModel.onBannerSlideChanged,
    entitiesPlayerTrailerModel.onBannerBecameVisible,
    entitiesPlayerTrailerModel.$shouldRenderTrailerPlayer,
    entitiesPlayerTrailerModel.$getSlotRefCallback,
    entitiesPlayerTrailerModel.$trailerContentId,
  ])

  const isBannerAutoScrollEnabled =
    getIsBannerAutoScrollEnabled(autoScrollEnabled ?? false) &&
    autoScrollEnabledForTrailer

  const handleBannerSlideChanged = useCallback(
    (slideIndex: number, contentIndex: number | null) => {
      if (locationKey && bannerId != null) {
        bannerSlide.saveBannerSlideFx({
          locationKey,
          bannerId: String(bannerId),
          slide: slideIndex,
        })
      }

      if (!content?.length || contentIndex == null) {
        onBannerSlideChanged()
        return
      }

      const item = content[contentIndex]
      if (!item?.content) {
        onBannerSlideChanged()
        return
      }

      const params = buildBannerVisibleParams(item, {
        autoScrollIn: autoScrollIn ?? 0,
        autoScrollEnabled,
        locked: getIsLockedFn(item.content),
        free: getIsFree(item.content),
      })

      onBannerSlideChanged(params ?? undefined)
    },
    [
      locationKey,
      bannerId,
      content,
      autoScrollIn,
      autoScrollEnabled,
      getIsLockedFn,
      onBannerSlideChanged,
    ]
  )

  const chooseBannerCard = useCallback(
    (item: BannerBundle): JSX.Element => {
      const cardContent = item.content ?? ({} as SubCategory)

      const {
        tabletBannerUrl: posterUrl,
        mobileBannerUrl: posterMobileUrl,
        resourceType,
        link,
      } = item

      const showHowToWatchButton = cardContent
        ? getIsShowHowToWatchButton(cardContent)
        : false
      const showSignInToWatchButton = cardContent
        ? getShowSignInToWatchButton(cardContent)
        : false
      // Movie and TV Show banners carry no description under the label line
      // (FP-4358); only the pin lock hint may still take the line's place.
      const showsDescription =
        item.contentType !== 'VOD' && item.contentType !== 'TV_SHOW'
      const getBannerSubtitle = showsDescription
        ? getLockedBannerSubtitle
        : getLockedBannerHint
      const description = cardContent ? getBannerSubtitle(cardContent) : ''
      const locked = cardContent ? getIsLockedFn(cardContent) : false
      const isFree = cardContent ? getIsFree(cardContent) : false
      const paid = cardContent ? isPaid(cardContent) : false
      const rented =
        cardContent && 'rented' in cardContent
          ? Boolean(cardContent.rented)
          : false
      const purchaseTitle =
        cardContent && 'labelPrice' in cardContent
          ? cardContent.labelPrice
          : undefined
      const blockedByAcl = cardContent.blockedByAcl

      const { name, resolution, dolbyAtmosAvailable } = cardContent
      const labels = getBannerContentLabels({
        contentType: item.contentType,
        content: cardContent,
        t,
      })
      const ageRatingLabel = getBannerAgeRatingLabel({
        contentType: item.contentType,
        content: cardContent,
      })
      const contentLabels =
        'contentLabels' in cardContent &&
        Array.isArray(cardContent.contentLabels)
          ? cardContent.contentLabels
          : getBannerTopContentLabels({ content: cardContent, t })

      const props = {
        title: name,
        description,
        labels,
        ageRatingLabel,
        contentLabels,
        quality: resolution,
        dolbyAtmos: dolbyAtmosAvailable,
        verticalVideo:
          'vertical' in cardContent && Boolean(cardContent.vertical),
        locked,
        showAclBlockedLabel: blockedByAcl,
        paid,
        rented,
        purchaseTitle,
        isFree,
        posterUrl,
        posterMobileUrl,
        horizontalCovers,
        verticalCovers,
        playTitle,
        trailerTitle,
        showHowToWatchButton,
        howToWatchTitle,
        showSignInToWatchButton,
        signInToWatchTitle,
        onSignInClick,
        onLockedClick,
        isGuest,
        favorites,
        onFavoriteClick,
        lockedTitle,
        detailsTitle,
        categoryId: placement.categoryId,
        collectionId: placement.collectionId,
        onlyFavorites: placement.onlyFavorites,
        categoryName: placement.categoryName,
      }

      const getTrailerProps = (
        contentId: number | undefined,
        contentType: TrailerContentType
      ) => {
        if (!contentId) return {}

        const visibleParams = buildBannerVisibleParams(item, {
          autoScrollIn: autoScrollIn ?? 0,
          autoScrollEnabled,
          locked,
          free: isFree,
        })
        if (!visibleParams || visibleParams.contentType !== contentType) {
          return {}
        }

        // Only the banner that owns the current trailer flow may render a
        // trailer slot or hide its poster — otherwise several banners compete
        // for the single portal target and playback is torn down (FP-4222).
        const ownsTrailer = trailerContentId === contentId

        return {
          onBecameVisible: () => {
            onBannerBecameVisible(visibleParams)
          },
          isTrailerPlayerVisible: isTrailerPlayerVisible && ownsTrailer,
          shouldRenderTrailerPlayer:
            shouldRenderTrailerPlayer && !locked && ownsTrailer,
          setRef: getSlotRefCallback(contentId),
        }
      }

      switch (item.contentType) {
        case 'VOD': {
          const trailerProps = getTrailerProps(
            cardContent.id,
            TrailerContentType.VOD
          )
          return <VodBannerCard {...props} {...trailerProps} item={item} />
        }
        case 'TV_CHANNEL':
          return <ChannelBannerCard {...props} item={item} />
        case 'TV_SHOW': {
          const trailerProps = getTrailerProps(
            cardContent.id,
            TrailerContentType.TVSHOW
          )
          return <ShowBannerCard {...props} {...trailerProps} item={item} />
        }
        case 'TV_SHOW_CATEGORY':
          return (
            <CategoryBannerCardTemplate
              detailsLink={`/content/series/categories/${cardContent.id}?routeName=Banners`}
              {...props}
            />
          )
        case 'TV_SHOW_SUBCATEGORY':
          return (
            <CategoryBannerCardTemplate
              detailsLink={`/content/series/categories/${(cardContent as SubCategory).parentCategoryId}/subcategories/${cardContent.id}?routeName=Banners`}
              {...props}
            />
          )
        case 'VOD_CATEGORY':
          return (
            <CategoryBannerCardTemplate
              detailsLink={`/content/movie/categories/${cardContent.id}?routeName=Banners`}
              {...props}
            />
          )
        case 'VOD_SUBCATEGORY':
          return (
            <CategoryBannerCardTemplate
              detailsLink={`/content/movie/categories/${(cardContent as SubCategory).parentCategoryId}/subcategories/${cardContent.id}?routeName=Banners`}
              {...props}
            />
          )
        case 'TV_CATEGORY':
          return (
            <CategoryBannerCardTemplate
              detailsLink={`/content/live/categories/${cardContent.id}?routeName=Banners`}
              {...props}
            />
          )
        case undefined:
        case null: {
          if (resourceType === ResourceTypes.EXTERNAL) {
            return (
              <BannerEmptyLarge
                link={link}
                posterUrl={posterUrl}
                posterMobileUrl={posterMobileUrl}
              />
            )
          }
          if (resourceType === ResourceTypes.INTERNAL) {
            return <></>
          }
          return (
            <BannerEmptyLarge
              posterUrl={posterUrl}
              posterMobileUrl={posterMobileUrl}
            />
          )
        }
        default: {
          return <></>
        }
      }
    },
    [
      autoScrollEnabled,
      autoScrollIn,
      detailsTitle,
      favorites,
      getIsLockedFn,
      getIsShowHowToWatchButton,
      getLockedBannerHint,
      getLockedBannerSubtitle,
      getShowSignInToWatchButton,
      getSlotRefCallback,
      horizontalCovers,
      howToWatchTitle,
      isGuest,
      isTrailerPlayerVisible,
      lockedTitle,
      onBannerBecameVisible,
      onFavoriteClick,
      onLockedClick,
      onSignInClick,
      placement,
      playTitle,
      trailerTitle,
      shouldRenderTrailerPlayer,
      signInToWatchTitle,
      t,
      trailerContentId,
      verticalCovers,
    ]
  )

  return {
    chooseBannerCard,
    handleBannerSlideChanged,
    isBannerAutoScrollEnabled,
    isAnyPaymentPopupOpened,
    goToNextSlideTrigger,
  }
}
